import classnames from "classnames";
import { hyphenate } from "neetocist";
import { MenuHorizontal, MenuVertical } from "neetoicons";
import { Button, Dropdown, Tooltip } from "neetoui";
import PropTypes from "prop-types";
import { mergeRight } from "ramda";
import { useTranslation } from "react-i18next";

import {
  BUTTON_STYLES,
  DEFAULT_TOOLTIP_PROPS,
  MENU_ITEM_TYPES,
  SIZES,
  STRATEGY,
} from "./constants";
import MenuItem from "./MenuItem";
import "./moredropdown.scss";

const { Menu } = Dropdown;

const MoreDropdown = ({
  dropdownButtonProps = {},
  isVertical = false,
  isDisabled = false,
  dropdownProps = {},
  menuTopChildren,
  menuBottomChildren,
  menuItems = [],
}) => {
  const { t } = useTranslation();

  const { isRounded: isRoundedButton, ...buttonProps } = dropdownButtonProps;

  const tooltipProps = mergeRight(
    DEFAULT_TOOLTIP_PROPS,
    dropdownButtonProps.tooltipProps
  );

  const targetIcon = isVertical ? MenuVertical : MenuHorizontal;

  const isDropdownDisabled = isDisabled || dropdownProps.disabled;
  if (isDropdownDisabled && !tooltipProps.disabled) {
    return (
      <Tooltip {...tooltipProps}>
        <span>
          <Button
            disabled
            data-dropdown-button-style="more-dropdown"
            data-testid="more-dropdown-button"
            icon={targetIcon}
            iconPosition="right"
            size="medium"
            style="text"
            className={classnames({
              "neeto-molecule-moredropdown--rounded": isRoundedButton,
            })}
            {...buttonProps}
          />
        </span>
      </Tooltip>
    );
  }

  return (
    <Dropdown
      className="flex"
      disabled={isDisabled}
      icon={targetIcon}
      zIndex={99999}
      buttonProps={{
        "data-dropdown-button-style": "more-dropdown",
        className: classnames(
          "min-h-0 flex-shrink-0 neeto-molecule-moredropdown-btn",
          { "neeto-molecule-moredropdown--rounded": isRoundedButton }
        ),
        style: "text",
        tooltipProps: {
          content: t("neetoMolecules.moreDropdown.tooltip"),
          position: "top",
          touch: ["hold", 500],
        },
        "aria-haspopup": "menu",
        "aria-label": t("neetoMolecules.moreDropdown.buttonAriaLabel"),
        ...buttonProps,
      }}
      {...dropdownProps}
    >
      <Menu
        aria-label={t("neetoMolecules.moreDropdown.menuAriaLabel")}
        role="menu"
      >
        {menuTopChildren}
        {menuItems.map(({ isVisible = true, key, ...menuItemProps }) => {
          if (!isVisible) return null;

          return (
            <MenuItem
              key={key ?? hyphenate(menuItemProps.label)}
              {...menuItemProps}
            />
          );
        })}
        {menuBottomChildren}
      </Menu>
    </Dropdown>
  );
};

MoreDropdown.propTypes = {
  /*
   * This props will be passed to the target button of the dropdown.
   */
  dropdownButtonProps: PropTypes.shape({
    style: PropTypes.oneOf(Object.values(BUTTON_STYLES)),
    size: PropTypes.oneOf(Object.values(SIZES)),
    iconSize: PropTypes.number,
    disabled: PropTypes.bool,
    isRounded: PropTypes.bool,
  }),
  /*
   * Whether to show a MenuVertical icon. By default the icon will be MenuHorizontal.
   */
  isVertical: PropTypes.bool,
  /*
   * Whether the dropdown is disabled or not.
   */
  isDisabled: PropTypes.bool,
  /*
   * This props will be passed to the dropdown.
   */
  dropdownProps: PropTypes.shape({
    strategy: PropTypes.oneOf(Object.values(STRATEGY)),
  }),
  /*
   * This children will be rendered on top of the dropdown menu.
   */
  menuTopChildren: PropTypes.node,
  /*
   * This children will be rendered on bottom of the dropdown menu.
   */
  menuBottomChildren: PropTypes.node,
  /*
   * Menu items inside the dropdown. Each item needs to have a `label`, `onClick` and `key`.
   */
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      onClick: PropTypes.func,
      key: PropTypes.string,
      type: PropTypes.oneOf(Object.values(MENU_ITEM_TYPES)),
      isVisible: PropTypes.bool,
      hasSubItems: PropTypes.bool,
      dropdownProps: PropTypes.shape({
        position: PropTypes.string,
        trigger: PropTypes.string,
      }),
    })
  ),
};

export default MoreDropdown;
